import React, { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import omit from 'lodash/omit';
import { Button, ButtonWithIcon } from '@els/els-react--button';
import { Flyout } from '@els/els-react--flyout';
import { PillWithIcon } from '@els/els-react--pill';
import { ValidatedTextArea } from '@els/els-react--text-input';
import AppStorage from '@Services/AppStorage';

import PromptStructureAttachment from './PromptStructureAttachment';
import { QUICK_SEARCH_TRACKING_BUTTON_TYPES, trackQuickSearchButtonClick } from '../analytics';

import './QuickSearchPromptInput.scss';

const MAX_QUERY_LENGTH = 512;
const PROMPT_LABEL_TEXT = 'Ask a text or structure based question';
const DRAW_BUTTON_DESCRIPTION_ID = 'quick-search-prompt-input-draw-description';
const DRAW_BUTTON_DESCRIPTION = 'Open structure editor to draw a chemical structure or reaction.';
const DRAW_BUTTON_DISABLED_TOOLTIP = 'Remove current structure to add a new one';
const SEARCH_BUTTON_MAX_LENGTH_DESCRIPTION_ID = 'quick-search-prompt-input-search-max-length-description';
const SEARCH_BUTTON_MAX_LENGTH_DESCRIPTION = 'maximum character length reached';
const QUICK_SEARCH_STRUCTURE_RETURN_FOCUS_KEY = 'quickSearchStructureReturnFocusTarget';
const QUICK_SEARCH_STRUCTURE_REMOVE_BUTTON_TARGET = 'removeStructureButton';
const PROMPT_LABEL_ID = 'quick-search-prompt-input-label';
const PROMPT_INPUT_ID = 'id-quick-search-input';

// Returns clear timeout function to be used in clear effect
function fixTextareaAutoHeight(textarea) {
  if (!textarea) {
    return () => {};
  }

  // Approach is the same as in Leyden's TextArea with autoHeight
  const timeoutId = setTimeout(() => {
    const style = textarea?.style;

    if (style) {
      style.height = 'auto';
      style.height = `${textarea.scrollHeight + 4}px`;
    }
  });

  return () => {
    clearTimeout(timeoutId);
  };
}

function QuickSearchPromptInput({
  promptInputFocusTrigger,
  value,
  onChange,
  onSearch,
  onDraw,
  onClearStructure,
  structurePreview,
  structureName,
  previewSize,
  inputProps,
  placeholder,
  headingAccessory,
}) {
  const [hasInteracted, setHasInteracted] = useState(Boolean(value));
  const [showEmptyError, setShowEmptyError] = useState(false);
  const textAreaRef = useRef(null);
  const removeStructureButtonRef = useRef(null);
  const previousPromptInputFocusTriggerRef = useRef(promptInputFocusTrigger);
  const previousStructurePreviewRef = useRef(structurePreview);
  const skipProgrammaticResizeRef = useRef(false);

  useEffect(() => {
    if (value && !hasInteracted) {
      setHasInteracted(true);
    }
  }, [value, hasInteracted]);

  // Leyden recalculates textarea auto-height on user input, but not when the controlled
  // value changes externally (for example, after applying a Search Tips example). Resize
  // only for those programmatic updates and leave normal typing on Leyden's native path.
  useEffect(() => {
    if (skipProgrammaticResizeRef.current) {
      skipProgrammaticResizeRef.current = false;
      return undefined;
    }

    const clearHeightFixFn = fixTextareaAutoHeight(textAreaRef.current);
    return () => clearHeightFixFn();
  }, [value]);

  // Fix height when structurePreview added/removed
  useEffect(() => {
    const clearHeightFixFn = fixTextareaAutoHeight(textAreaRef.current);
    return () => clearHeightFixFn();
  }, [structurePreview]);

  useEffect(() => {
    if (previousPromptInputFocusTriggerRef.current === promptInputFocusTrigger) {
      return;
    }

    previousPromptInputFocusTriggerRef.current = promptInputFocusTrigger;
    setTimeout(() => {
      textAreaRef.current?.focus?.();
    }, 0);
  }, [promptInputFocusTrigger]);

  useEffect(() => {
    const pendingFocusTarget = AppStorage.instance.getFromSessionStorage(QUICK_SEARCH_STRUCTURE_RETURN_FOCUS_KEY);

    if (pendingFocusTarget === QUICK_SEARCH_STRUCTURE_REMOVE_BUTTON_TARGET && structurePreview) {
      AppStorage.instance.removeFromSessionStorage(QUICK_SEARCH_STRUCTURE_RETURN_FOCUS_KEY);
      removeStructureButtonRef.current?.focus?.();
    }

    if (previousStructurePreviewRef.current && !structurePreview) {
      textAreaRef.current?.focus?.();
    }

    previousStructurePreviewRef.current = structurePreview;
  }, [structurePreview]);

  const handleOpenStructureEditor = () => {
    AppStorage.instance.setToSessionStorage(QUICK_SEARCH_STRUCTURE_RETURN_FOCUS_KEY, QUICK_SEARCH_STRUCTURE_REMOVE_BUTTON_TARGET);
    onDraw();
  };

  const handleDrawButtonClick = () => {
    trackQuickSearchButtonClick(QUICK_SEARCH_TRACKING_BUTTON_TYPES.drawButton);
    handleOpenStructureEditor();
  };

  const textareaClassName = useMemo(() => {
    const classes = ['quick-search-prompt-input__textarea'];

    if (structurePreview) {
      classes.push('quick-search-prompt-input__textarea--with-structure');
    }

    if (showEmptyError) {
      classes.push('quick-search-prompt-input__textarea--empty-error');
    }

    return classes.join(' ');
  }, [showEmptyError, structurePreview]);

  const validator = (nextValue) => {
    if (!nextValue) {
      return null;
    }

    if (nextValue.length >= MAX_QUERY_LENGTH) {
      return [{ isValid: false, message: 'Maximum character length reached. Edit to continue typing.' }];
    }

    return null;
  };

  const handleSearch = () => {
    if (!value?.trim() && !structurePreview) {
      setShowEmptyError(true);
      textAreaRef.current?.focus?.();
      return;
    }

    setShowEmptyError(false);
    onSearch();
  };

  const showCount = hasInteracted || Boolean(structurePreview);
  const drawDisabled = Boolean(structurePreview);
  const isMaxLengthReached = Boolean(value) && value.length >= MAX_QUERY_LENGTH;
  const promptLabel = (
    <label htmlFor={PROMPT_INPUT_ID} id={PROMPT_LABEL_ID} className="quick-search-prompt-input__label-content u-els-font-size-h4">
      <span className="quick-search-prompt-input__label-text">{PROMPT_LABEL_TEXT}</span>
      <sup aria-hidden="true">
        <PillWithIcon
          iconProps={{
            sprite: 'AI',
          }}
          iconColor="text"
          iconRight
          condensed
          pillColor="extended-blue-0"
          textColor="text"
          className="quick-search-prompt-input__pill u-els-font-size-base">
          AI
        </PillWithIcon>
      </sup>
      <span className="u-els-hide-visually">AI enhanced</span>
    </label>
  );
  const drawButton = (
    <ButtonWithIcon
      aria-describedby={!drawDisabled ? DRAW_BUTTON_DESCRIPTION_ID : undefined}
      data-e2e="e2e-build-query-structuredrawing-draw"
      disabled={drawDisabled}
      onClick={handleDrawButtonClick}
      sprite="DrawStructureNew"
      type="borderless">
      Draw
    </ButtonWithIcon>
  );
  const promptToolbar = (
    <div className="quick-search-prompt-input__toolbar">
      <div className="quick-search-prompt-input__toolbar-left">
        {!drawDisabled ? (
          <span id={DRAW_BUTTON_DESCRIPTION_ID} className="u-els-hide-visually">
            {DRAW_BUTTON_DESCRIPTION}
          </span>
        ) : null}
        {drawDisabled ? (
          <Flyout trigger="hover" flyout={DRAW_BUTTON_DISABLED_TOOLTIP} placement="bottom" theme="simple">
            {drawButton}
          </Flyout>
        ) : (
          drawButton
        )}
      </div>
      <div className="quick-search-prompt-input__toolbar-right">
        {isMaxLengthReached ? (
          <span id={SEARCH_BUTTON_MAX_LENGTH_DESCRIPTION_ID} className="u-els-hide-visually">
            {SEARCH_BUTTON_MAX_LENGTH_DESCRIPTION}
          </span>
        ) : null}
        <Button
          aria-describedby={isMaxLengthReached ? SEARCH_BUTTON_MAX_LENGTH_DESCRIPTION_ID : undefined}
          data-e2e="search query"
          onClick={handleSearch}
          disabled={isMaxLengthReached}>
          Search
        </Button>
      </div>
    </div>
  );

  const textareaInputProps = omit(inputProps, 'role');

  return (
    <div className="quick-search-prompt-input" role="group" data-testid="quick-search-prompt-input">
      <div className="quick-search-prompt-input__heading">
        {promptLabel}
        {headingAccessory ? <div className="quick-search-prompt-input__label-accessory">{headingAccessory}</div> : null}
      </div>
      <div className="quick-search-prompt-input__shell">
        {structurePreview ? (
          <PromptStructureAttachment
            onClear={onClearStructure}
            onEdit={handleOpenStructureEditor}
            previewSize={previewSize}
            removeButtonRef={removeStructureButtonRef}
            structureName={structureName}
            structurePreview={structurePreview}
          />
        ) : null}
        {/* Leyden hides its own label by converting the string label into aria-label.
            Keep this external label as the visible and programmatic name instead, so the structure preview can sit
            inside the field border without depending on Leyden's rendered label height. */}
        <ValidatedTextArea
          data-e2e="quick-search-input"
          {...textareaInputProps}
          id={PROMPT_INPUT_ID}
          className={textareaClassName}
          inputRef={textAreaRef}
          label={PROMPT_LABEL_TEXT}
          max={MAX_QUERY_LENGTH}
          onChange={(event) => {
            const nextValue = event?.target?.value || '';
            if (!hasInteracted && nextValue) {
              setHasInteracted(true);
            }
            if (showEmptyError && (nextValue.trim() || structurePreview)) {
              setShowEmptyError(false);
            }
            skipProgrammaticResizeRef.current = true;
            onChange(nextValue);
          }}
          onKeyDown={(event) => {
            inputProps?.onKeyDown?.(event);
            if (event.defaultPrevented) {
              return;
            }

            if (event.key === 'Enter' && !event.shiftKey) {
              event.preventDefault();
              handleSearch();
            }
          }}
          onlyValidateWhenTouched={false}
          shouldHideLabel
          aria-label={undefined}
          aria-labelledby={PROMPT_LABEL_ID}
          placeholder={structurePreview ? PROMPT_LABEL_TEXT : placeholder}
          rows={1}
          showCount={showCount}
          countThreshold={0}
          toolbar={promptToolbar}
          validator={validator}
          value={value}
          autoHeight
          maxHeight={structurePreview ? '27.25rem' : ' 13.75rem'}
          minHeight={structurePreview ? '20.25rem' : ' 6.625rem'}>
          {showEmptyError ? (
            <ValidatedTextArea.Message type="error" id="quick-search-prompt-input-empty-error-id">
              Search field is empty. Enter a query to continue.
            </ValidatedTextArea.Message>
          ) : null}
        </ValidatedTextArea>
      </div>
    </div>
  );
}

QuickSearchPromptInput.propTypes = {
  promptInputFocusTrigger: PropTypes.number,
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  onSearch: PropTypes.func.isRequired,
  onDraw: PropTypes.func.isRequired,
  onClearStructure: PropTypes.func.isRequired,
  structurePreview: PropTypes.string,
  structureName: PropTypes.oneOfType([PropTypes.string, PropTypes.shape({ name: PropTypes.string, isLinearFormula: PropTypes.bool })]),
  previewSize: PropTypes.object,
  inputProps: PropTypes.object,
  placeholder: PropTypes.string,
  headingAccessory: PropTypes.node,
};

QuickSearchPromptInput.defaultProps = {
  promptInputFocusTrigger: 0,
};

export default QuickSearchPromptInput;
